import type { RecommendedActionCode, RiskLevel } from "../types";

interface NetworkScoreNode {
  id: string;
  riskLevel: RiskLevel;
  isRisky: boolean;
  recommendedMeasure: RecommendedActionCode | null;
}

const RISK_POINTS: Readonly<Record<RiskLevel, number>> = {
  high: 15,
  medium: 8,
  low: 5,
};

/**
 * Normalized Network Map score (0-100). Canon: spec 11 §15.2.
 *
 * Each risky node contributes riskPoints(level) for being correctly marked and
 * an extra 10 for the matching recommended measure; maxPossible sums those over
 * the risky nodes. A non-risky node marked as risky is a false positive (-5).
 * A map without risky nodes scores 100.
 */
export function calculateNetworkMapScore(
  nodes: readonly NetworkScoreNode[],
  markedRiskyIds: ReadonlySet<string>,
  chosenMeasures: Readonly<Record<string, RecommendedActionCode>>,
): number {
  const risky = nodes.filter((n) => n.isRisky);
  if (risky.length === 0) {
    return 100;
  }
  const maxPossible = risky.reduce(
    (sum, n) => sum + RISK_POINTS[n.riskLevel] + 10,
    0,
  );
  let earned = 0;
  for (const n of nodes) {
    const marked = markedRiskyIds.has(n.id);
    if (n.isRisky && marked) {
      earned += RISK_POINTS[n.riskLevel];
      if (chosenMeasures[n.id] && chosenMeasures[n.id] === n.recommendedMeasure) {
        earned += 10;
      }
    } else if (!n.isRisky && marked) {
      earned -= 5;
    }
  }
  return Math.min(100, Math.max(0, Math.round((100 * earned) / maxPossible)));
}
